import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import clipboard from 'clipboard-polyfill';
import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs';

import {
  DIALOG_DATA,
  DialogRef,
  DialogService,
  DialogSize,
} from '../dialog/public-api';
import { CodeEditorIntl } from './code-editor-intl.service';
import { MonacoProviderService } from './monaco-provider.service';

export interface CodeEditorActionsConfig {
  diffMode?: boolean;
  recover?: boolean;
  clear?: boolean;
  find?: boolean;
  copy?: boolean;
  theme?: boolean;
  fullscreen?: boolean;
  export?: boolean;
  import?: boolean;
}

const DEFAULT_ACTIONS_CONFIG: CodeEditorActionsConfig = {
  diffMode: true,
  recover: true,
  clear: true,
  find: true,
  copy: true,
  theme: true,
  fullscreen: true,
  export: true,
  import: true,
};

/**
 * Code editor.
 */
@Component({
  selector: 'aui-code-editor',
  templateUrl: './code-editor.component.html',
  styleUrls: ['./code-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  preserveWhitespaces: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeEditorComponent),
      multi: true,
    },
  ],
})
export class CodeEditorComponent implements ControlValueAccessor, OnDestroy {
  private editor: import('monaco-editor').editor.IStandaloneCodeEditor;
  protected _options = {};
  protected _actionsConfig: CodeEditorActionsConfig = DEFAULT_ACTIONS_CONFIG;
  protected _originalValue = '';
  protected _diffMode = false;
  protected _modelUri: string;
  protected _monacoEditorChanged: EventEmitter<
    import('monaco-editor').editor.IStandaloneCodeEditor
  > = new EventEmitter();
  protected _controlSubscription = Subscription.EMPTY;

  // FormControl to be bound to the template.
  // The source and fullscreen editor shares the same formcontrol.
  protected _valueControl = new FormControl('');

  /**
   * Raw Monaco editor options
   */
  @Input()
  get options(): import('monaco-editor').editor.IEditorConstructionOptions {
    if (this.source) {
      return this.source.options;
    } else {
      return this._options;
    }
  }

  set options(
    options: import('monaco-editor').editor.IEditorConstructionOptions,
  ) {
    if (this.source) {
      this.source.options = options;
    } else {
      this._options = options;
    }
  }

  /**
   * Value input. Optinally, you could access the model via ControlValueAccessor
   * like ngModel.
   */
  @Input()
  set value(value: string) {
    if (this.source) {
      this.source.value = value;
    } else {
      this.writeValue(value);
    }
  }

  get value() {
    if (this.source) {
      return this.source.value;
    } else {
      return this.valueControl.value;
    }
  }

  /**
   * The original value to recover
   */
  @Input()
  get originalValue() {
    if (this.source) {
      return this.source.originalValue;
    } else {
      return this._originalValue;
    }
  }

  set originalValue(originalValue: string) {
    if (this.source) {
      this.source.originalValue = originalValue;
    } else {
      this._originalValue = originalValue;
    }
  }

  /**
   * Configure action buttons display. All actions are enabled by default.
   */
  @Input()
  get actionsConfig() {
    if (this.source) {
      return this.source.actionsConfig;
    } else {
      return this._actionsConfig;
    }
  }

  set actionsConfig(actionsConfig: CodeEditorActionsConfig) {
    if (this.source) {
      this.source.actionsConfig = actionsConfig;
    } else {
      this._actionsConfig = { ...DEFAULT_ACTIONS_CONFIG, ...actionsConfig };
      this.cdr.markForCheck();
    }
  }

  /**
   * Should show as diff mode or not
   */
  @Input()
  get diffMode(): boolean {
    if (this.source) {
      return this.source.diffMode;
    } else {
      return this._diffMode;
    }
  }

  set diffMode(diffMode: boolean) {
    if (this.source) {
      this.source.diffMode = diffMode;
    } else {
      this._diffMode = diffMode;
    }
  }

  /**
   * The URI which will be assigned to monaco-editor's model.
   */
  @Input()
  get modelUri(): string {
    if (this.source) {
      return this.source.modelUri;
    } else {
      return this._modelUri;
    }
  }

  set modelUri(modelUri: string) {
    if (this.source) {
      this.source.modelUri = modelUri;
    } else {
      this._modelUri = modelUri;
    }
  }

  /**
   * The FormControl object is shared between this form and its source.
   */
  get valueControl(): FormControl {
    if (this.source) {
      return this.source.valueControl;
    } else {
      return this._valueControl;
    }
  }

  /**
   * Events emitted when monaco editor changed.
   * User may want to use this to have advanced tunnings on the editor.
   */
  @Output()
  get monacoEditorChanged(): EventEmitter<
    import('monaco-editor').editor.IStandaloneCodeEditor
  > {
    if (this.source) {
      return this.source.monacoEditorChanged;
    } else {
      return this._monacoEditorChanged;
    }
  }

  /**
   * The fullscreen dialog.
   */
  fullscreenDialog: DialogRef<CodeEditorComponent>;

  @ViewChild('fileUploadInput')
  fileUploadInputElementRef: ElementRef<HTMLInputElement>;

  get modeId() {
    return (
      this.editor &&
      this.editor.getModel() &&
      this.editor.getModel().getModeId()
    );
  }

  constructor(
    public monacoProvider: MonacoProviderService,
    public cdr: ChangeDetectorRef,
    public codeEditorIntl: CodeEditorIntl,
    @Optional() public dialog: DialogService,
    @Optional()
    @Inject(DIALOG_DATA)
    public source: CodeEditorComponent,
  ) {
    // If the provided DIALOG_DATA is not CodeEditorComponent, we believe
    // this code editor instance is not opened within a maximum dialog and
    // it should be reset to null.
    if (!(this.source instanceof CodeEditorComponent)) {
      this.source = undefined;
    }

    // Handle input changes by user
    this._controlSubscription = this.valueControl.valueChanges.subscribe(
      value => this.onValueChange(value),
    );
  }

  onThemeChange(event: Event) {
    const selectEl: HTMLSelectElement = event.target as HTMLSelectElement;
    const theme = selectEl.selectedOptions[0].value;
    this.monacoProvider.changeTheme(theme);
    event.stopPropagation();
  }

  onMonacoEditorChanged(
    editor: import('monaco-editor').editor.IStandaloneCodeEditor,
  ) {
    this.editor = editor;
    this.monacoEditorChanged.emit(editor);
  }

  onClearClicked() {
    this.valueControl.setValue('');
  }

  onExportClicked() {
    const lang = this.monacoProvider.getLanguageExtensionPoint(
      this.options.language,
    );

    const fileExtension = (lang && lang.extensions[0]) || '.txt';
    const mimeType = (lang && lang.mimetypes[0]) || 'text/plain';

    const blob = new Blob([this.value], { type: mimeType + ';charset=utf-8' });

    saveAs(blob, new Date().toISOString() + fileExtension);
  }

  onImportFileChanged(_event: Event) {
    const reader = new FileReader();
    const el = this.fileUploadInputElementRef.nativeElement;

    // TODO: handle possible errors?
    reader.onloadend = () => {
      this.valueControl.setValue(reader.result.toString());
      el.value = '';
    };

    reader.readAsText(el.files[0]);
  }

  onRecoverClicked() {
    this.valueControl.setValue(this.originalValue);
  }

  onFindClicked(): import('monaco-editor').Promise<void, any> {
    return this.checkActionAndRun('actions.find');
  }

  canFullscreen() {
    return !!this.dialog && this.actionsConfig.fullscreen;
  }

  onFoldAllClicked(): import('monaco-editor').Promise<void, any> {
    return this.checkActionAndRun('editor.foldAll');
  }

  getLanguageExtensions() {
    if (this.editor) {
      const lang = this.monacoProvider.getLanguageExtensionPoint(
        this.options.language,
      );
      if (lang) {
        return lang.extensions;
      }
    }
  }

  getInputFileAccepts() {
    const extensions = this.getLanguageExtensions();
    return extensions && extensions.join(', ');
  }

  private checkActionAndRun(actionName: string) {
    const action = this.getEditorAction(actionName);
    return action && action.run();
  }

  private getEditorAction(actionName: string) {
    return this.editor && this.editor.getAction(actionName);
  }

  onCopyClicked() {
    clipboard.writeText(this.value);
  }

  // Following are APIs required by ControlValueAccessor
  onChange = (_: string) => {};
  onTouched = () => {};

  writeValue(value: string): void {
    if (this.source) {
      this.source.writeValue(value);
    } else {
      this._valueControl.setValue(value, { emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  toggleFullscreen() {
    if (this.fullscreenDialog) {
      this.fullscreenDialog.close();
      this.fullscreenDialog = undefined;
      this.cdr.markForCheck();
    } else if (this.source) {
      this.source.toggleFullscreen();
    } else {
      this.fullscreenDialog = this.dialog.open(CodeEditorComponent, {
        size: DialogSize.Fullscreen,
        data: this,
      });
      this.fullscreenDialog.afterClosed().subscribe(() => {
        this.fullscreenDialog = undefined;
      });
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    if (this.fullscreenDialog) {
      this.fullscreenDialog.close();
    }
    this.source = undefined;
    this._controlSubscription.unsubscribe();
  }

  /**
   * Handles user input events:
   */
  private onValueChange(value: string) {
    if (this.source) {
      this.source.onValueChange(value);
    } else {
      this.onChange(value);
    }

    this.cdr.markForCheck();
  }
}
